<script setup>
import ConnectorIcon from 'dashboard/components-next/agents/ConnectorIcon.vue';
// Node customizado do canvas. Cada FAMÍLIA tem uma forma (o olho aprende 5
// formas, não 30 ícones):
//   trigger (Início/Prospecção) = pílula, só saída
//   agent = herói: card maior com avatar, saída de fluxo (direita, azul) e
//           porta de ferramentas (embaixo, âmbar)
//   flow (Enviar/Fim) = card branco
//   logic (Condição/Classificar/Proteções/Aprovação) = cabeçalho colorido e
//           uma alça rotulada por ramo
//   tool (tool_*, HTTP, Ligação) = card compacto tintado com a etiqueta da
//           categoria da paleta; entrada âmbar em cima e saída "Depois" à
//           direita (liga em outra ferramenta, Enviar, Fim, Condição…)
//   note = post-it, sem alça
import { computed, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { Handle, Position } from '@vue-flow/core';
import { NODE_META, CATEGORY_OF, familyOf } from './nodeMeta';

const props = defineProps({
  // Vue Flow passa o id do node.
  id: { type: String, default: '' },
  data: { type: Object, required: true },
  // Vue Flow passa o estado de seleção do node.
  selected: { type: Boolean, default: false },
});

const { t } = useI18n();

const kind = computed(() => props.data.kind || '');
const meta = computed(() => NODE_META[kind.value] || {});
const family = computed(() => familyOf(kind.value));
const category = computed(() => CATEGORY_OF[kind.value] || null);
// Ferramenta: a cor vem da CATEGORIA (não do kind), pra card e etiqueta
// baterem com a paleta. Demais: cor do node (salva) ou do meta.
const color = computed(() => {
  if (family.value === 'tool' && category.value) return category.value.color;
  return props.data.color || meta.value.color || '#9aa0a6';
});
const title = computed(
  () => props.data.label || meta.value.label || kind.value
);
// Gatilhos não têm entrada.
const hasInput = computed(() => family.value !== 'trigger');
// Ferramenta desativada (toggle no inspector): card apagado + badge.
const isToolOff = computed(
  () => family.value === 'tool' && props.data.enabled === false
);
const initial = computed(() =>
  (title.value || 'A').trim().charAt(0).toUpperCase()
);
// Nome COMERCIAL do cérebro — nunca o id do modelo (whitelabel), igual ao
// AgentCard: gpt-5.5 = Lia 5.1, gpt-5.6-luna = Full-v3, o resto é Full-v2.
const brainLabel = computed(() => {
  const d = props.data;
  if (!d.model) return '';
  if (d.model === 'gpt-5.5') return 'Lia 5.1';
  if (d.model === 'gpt-6-luna') return t('LIA_FULL_V4_NAME');
  if (d.model === 'gpt-5.6-luna') return t('AI_AGENTS.FLOW.LIA.FULL_V3_NAME');
  return t('AI_AGENTS.FLOW.LIA.FULL_NAME');
});

// "Solto" = sem nenhuma ligação no fluxo (vem do canvas via provide).
const floatingNodeIds = inject('floatingNodeIds', null);
const isFloating = computed(
  () => floatingNodeIds?.value?.has(props.id) || false
);

// Fundo tintado suave a partir de um hex (#RRGGBB + alpha).
const tint = (hex, alpha = '1f') => `${hex}${alpha}`;

const TONE_HEX = {
  default: '#9aa0a6',
  info: '#4285f4',
  success: '#34a853',
  warn: '#f9ab00',
  danger: '#ea4335',
};
// Cores dos ramos do Classificar (cicla).
const BRANCH_COLORS = ['#4285f4', '#00897b', '#f9ab00', '#7e57c2', '#6b7a90'];

// Badges derivados da config do node — dão um resumo visual do que ele faz.
const badges = computed(() => {
  const d = props.data;
  const out = Array.isArray(d.badges) ? [...d.badges] : [];
  switch (d.kind) {
    case 'agent':
      if (d.reasoningEffort && d.reasoningEffort !== 'off') {
        out.push({ label: t('AI_AGENTS.FLOW.BADGES.REASONING'), tone: 'info' });
      }
      break;
    case 'send':
      if (d.responseFormat === 'audio') {
        out.push({ label: t('AI_AGENTS.FLOW.BADGES.AUDIO'), tone: 'info' });
      } else if (d.responseFormat === 'both') {
        out.push({
          label: t('AI_AGENTS.FLOW.BADGES.TEXT_AUDIO'),
          tone: 'info',
        });
      }
      break;
    case 'tool_escalate':
      if (d.escalateTo === 'team') {
        out.push({ label: t('AI_AGENTS.FLOW.BADGES.TEAM'), tone: 'warn' });
      } else if (d.escalateTo === 'user') {
        out.push({ label: t('AI_AGENTS.FLOW.BADGES.PERSON'), tone: 'warn' });
      }
      break;
    case 'http_request':
      // Integração de template ainda com endpoint placeholder -> avisa que
      // precisa plugar a URL real do sistema do cliente.
      if ((d.endpoint || '').includes('SEU-SISTEMA')) {
        out.push({ label: t('AI_AGENTS.FLOW.BADGES.CONFIGURE'), tone: 'warn' });
      }
      break;
    case 'classify': {
      const n = (d.categories || []).filter(c => c && c.name).length;
      if (n) {
        out.push({
          label: `${n} ${t('AI_AGENTS.FLOW.BADGES.CATEGORIES')}`,
          tone: 'default',
        });
      }
      break;
    }
    default:
      break;
  }
  if (isToolOff.value) {
    out.push({ label: t('AI_AGENTS.FLOW.TOOLS.OFF'), tone: 'default' });
  }
  return out;
});

// Saídas do node (cada uma vira uma alça conectável à direita).
// { id, label, color } — id null = alça padrão.
const outputs = computed(() => {
  const d = props.data;
  switch (d.kind) {
    case 'end':
      return [];
    case 'guardrails':
      return [
        { id: 'ok', label: t('AI_AGENTS.FLOW.OK'), color: TONE_HEX.success },
        {
          id: 'blocked',
          label: t('AI_AGENTS.FLOW.BLOCKED'),
          color: TONE_HEX.danger,
        },
      ];
    case 'if_else':
      return [
        { id: 'true', label: d.trueLabel || 'Sim', color: TONE_HEX.success },
        { id: 'false', label: d.falseLabel || 'Não', color: TONE_HEX.danger },
      ];
    case 'classify': {
      const cats = (d.categories || []).filter(c => c && c.name);
      if (cats.length) {
        return cats.map((c, i) => ({
          id: `cat:${c.name}`,
          label: c.name,
          color: BRANCH_COLORS[i % BRANCH_COLORS.length],
        }));
      }
      return [{ id: null, label: null, color: color.value }];
    }
    default:
      return [{ id: null, label: null, color: color.value }];
  }
});
const isMulti = computed(() => outputs.value.length > 1);

const handleBase = '!size-2.5 !rounded-full !border-2 !border-white !shadow-sm';
const flowHandleClass = `${handleBase} !bg-[#4285f4]`;
// Porta/alça de ferramentas: âmbar, pra deixar claro que ali é ferramenta —
// não é a continuação do fluxo.
const toolsHandleClass = `${handleBase} !bg-[#f9ab00]`;

const cardClass = computed(() => {
  if (props.selected) return 'border-n-brand ring-2 ring-n-brand/40';
  if (isFloating.value) return 'border-dashed border-n-amber-8';
  return 'border-n-weak';
});
</script>

<template>
  <!-- ===================== NOTA (post-it) ===================== -->
  <div
    v-if="family === 'note'"
    class="w-[190px] -rotate-1 rounded-sm border px-3 py-2.5 shadow-md"
    :class="selected ? 'ring-2 ring-n-brand/40' : ''"
    :style="{
      backgroundColor: tint(data.color || '#ffc53d', '33'),
      borderColor: tint(data.color || '#ffc53d', '66'),
    }"
  >
    <p class="whitespace-pre-line text-xs leading-[17px] text-n-slate-12">
      {{ data.content || title }}
    </p>
  </div>

  <!-- ===================== GATILHO (pílula) ===================== -->
  <div
    v-else-if="family === 'trigger'"
    class="relative w-[200px] rounded-full border py-2 pl-2.5 pr-3.5 shadow-sm transition-all hover:shadow-md"
    :class="selected ? 'ring-2 ring-n-brand/40' : ''"
    :style="{
      backgroundColor: tint(color, '14'),
      borderColor: tint(color, '55'),
    }"
  >
    <div class="flex items-center gap-2.5">
      <span
        class="flex size-8 shrink-0 items-center justify-center rounded-full text-white"
        :style="{ backgroundColor: color }"
      >
        <span :class="meta.icon || 'i-lucide-play'" class="text-base" />
      </span>
      <div class="min-w-0">
        <p
          class="truncate text-[13px] font-bold leading-[18px] text-n-slate-12"
        >
          {{ title }}
        </p>
        <p
          v-if="data.subtitle"
          class="truncate text-[11px] leading-[14px] text-n-slate-10"
        >
          {{ data.subtitle }}
        </p>
      </div>
    </div>
    <Handle type="source" :position="Position.Right" :class="flowHandleClass" />
  </div>

  <!-- ===================== AGENTE (herói) ===================== -->
  <div
    v-else-if="family === 'agent'"
    class="relative w-[236px] rounded-2xl border-2 bg-n-solid-1 p-3 transition-all hover:shadow-lg"
    :class="selected ? 'ring-2 ring-n-brand/40' : ''"
    :style="{
      borderColor: color,
      boxShadow: `0 8px 16px -6px ${tint(color, '55')}`,
    }"
  >
    <span
      v-if="isFloating"
      v-tooltip="t('AI_AGENTS.FLOW.NODE_DISCONNECTED')"
      class="absolute -right-1.5 -top-1.5 z-10 flex size-4 items-center justify-center rounded-full bg-n-amber-9 text-white shadow"
    >
      <span class="i-lucide-unlink text-[9px]" />
    </span>
    <Handle type="target" :position="Position.Left" :class="flowHandleClass" />
    <div class="flex items-center gap-2.5">
      <span
        class="flex size-10 shrink-0 items-center justify-center rounded-full text-[15px] font-bold text-white"
        :style="{ backgroundColor: color }"
      >
        {{ initial }}
      </span>
      <div class="min-w-0">
        <p class="truncate text-sm font-bold leading-[18px] text-n-slate-12">
          {{ title }}
        </p>
        <p class="truncate text-[11px] leading-[14px] text-n-slate-10">
          {{ meta.label
          }}<template v-if="brainLabel"> · {{ brainLabel }}</template>
        </p>
      </div>
    </div>
    <div v-if="badges.length" class="mt-2.5 flex flex-wrap gap-1">
      <span
        v-for="(b, i) in badges"
        :key="i"
        class="rounded-md px-1.5 py-0.5 text-[10px] font-medium leading-3"
        :style="{
          backgroundColor: tint(TONE_HEX[b.tone] || TONE_HEX.default, '24'),
          color: TONE_HEX[b.tone] || TONE_HEX.default,
        }"
      >
        {{ b.label }}
      </span>
    </div>
    <!-- Duas saídas tipadas: fluxo à direita, ferramentas embaixo. -->
    <Handle
      v-tooltip.right="t('AI_AGENTS.FLOW.OUT_FLOW')"
      type="source"
      :position="Position.Right"
      :class="flowHandleClass"
    />
    <Handle
      id="tools"
      v-tooltip.bottom="t('AI_AGENTS.FLOW.OUT_TOOLS')"
      type="source"
      :position="Position.Bottom"
      :class="toolsHandleClass"
    />
  </div>

  <!-- ===================== LÓGICA (cabeçalho + ramos) ===================== -->
  <div
    v-else-if="family === 'logic'"
    class="relative w-[214px] rounded-[10px] border bg-n-solid-1 shadow-md transition-all hover:shadow-lg"
    :class="cardClass"
  >
    <span
      v-if="isFloating"
      v-tooltip="t('AI_AGENTS.FLOW.NODE_DISCONNECTED')"
      class="absolute -right-1.5 -top-1.5 z-10 flex size-4 items-center justify-center rounded-full bg-n-amber-9 text-white shadow"
    >
      <span class="i-lucide-unlink text-[9px]" />
    </span>
    <Handle
      type="target"
      :position="Position.Left"
      :class="flowHandleClass"
      class="!top-[18px]"
    />
    <div
      class="flex items-center gap-2 rounded-t-[9px] px-3 py-2 text-white"
      :style="{ backgroundColor: color }"
    >
      <span :class="meta.icon || 'i-lucide-git-fork'" class="text-[15px]" />
      <p class="truncate text-[13px] font-bold leading-[18px]">{{ title }}</p>
    </div>
    <p
      v-if="data.subtitle"
      class="truncate px-3 pt-2 text-[11px] leading-[14px] text-n-slate-10"
    >
      {{ data.subtitle }}
    </p>
    <div v-if="badges.length" class="flex flex-wrap gap-1 px-3 pt-2">
      <span
        v-for="(b, i) in badges"
        :key="i"
        class="rounded-md px-1.5 py-0.5 text-[10px] font-medium leading-3"
        :style="{
          backgroundColor: tint(TONE_HEX[b.tone] || TONE_HEX.default, '24'),
          color: TONE_HEX[b.tone] || TONE_HEX.default,
        }"
      >
        {{ b.label }}
      </span>
    </div>
    <!-- Ramos: uma linha (bolinha + rótulo + alça) por saída -->
    <div v-if="isMulti" class="flex flex-col py-1.5">
      <div
        v-for="o in outputs"
        :key="o.id"
        class="relative flex items-center gap-1.5 px-3 py-[5px] text-[11px] font-medium"
        :style="{ color: o.color }"
      >
        <span
          class="size-1.5 shrink-0 rounded-full"
          :style="{ backgroundColor: o.color }"
        />
        <span class="truncate">{{ o.label }}</span>
        <Handle
          :id="o.id"
          type="source"
          :position="Position.Right"
          :class="handleBase"
          class="!top-1/2"
          :style="{ backgroundColor: o.color }"
        />
      </div>
    </div>
    <!-- Saída única (Aprovação): alça no rodapé -->
    <div v-else class="relative h-3">
      <Handle
        type="source"
        :position="Position.Right"
        :class="flowHandleClass"
        class="!top-0"
      />
    </div>
  </div>

  <!-- ===================== FERRAMENTA (compacto tintado) ===================== -->
  <div
    v-else-if="family === 'tool'"
    class="relative w-[188px] rounded-xl border px-2.5 py-2 shadow-sm transition-all hover:shadow-md"
    :class="[
      selected ? 'ring-2 ring-n-brand/40' : '',
      isToolOff ? 'opacity-50 grayscale' : '',
    ]"
    :style="{
      backgroundColor: tint(color, '0d'),
      borderColor: tint(color, '40'),
    }"
  >
    <Handle type="target" :position="Position.Top" :class="toolsHandleClass" />
    <p
      v-if="category"
      class="mb-1.5 text-[9px] font-bold uppercase leading-3 tracking-[.08em]"
      :style="{ color }"
    >
      {{ category.label }}
    </p>
    <div class="flex items-center gap-2">
      <span
        class="flex size-[26px] shrink-0 items-center justify-center rounded-lg border bg-n-solid-1"
        :style="{ borderColor: tint(color, '40'), color }"
      >
        <ConnectorIcon
          :type="kind"
          :icon="meta.icon || 'i-lucide-wrench'"
          class="text-sm"
        />
      </span>
      <div class="min-w-0">
        <p class="truncate text-xs font-semibold leading-4 text-n-slate-12">
          {{ title }}
        </p>
        <p
          v-if="data.subtitle"
          class="truncate text-[10px] leading-[13px] text-n-slate-10"
        >
          {{ data.subtitle }}
        </p>
      </div>
    </div>
    <div v-if="badges.length" class="mt-1.5 flex flex-wrap gap-1">
      <span
        v-for="(b, i) in badges"
        :key="i"
        class="rounded-md px-1.5 py-0.5 text-[10px] font-medium leading-3"
        :style="{
          backgroundColor: tint(TONE_HEX[b.tone] || TONE_HEX.default, '24'),
          color: TONE_HEX[b.tone] || TONE_HEX.default,
        }"
      >
        {{ b.label }}
      </span>
    </div>
    <!-- Saída "Depois": o que fazer logo depois de usar a ferramenta. -->
    <Handle
      id="after"
      v-tooltip.right="t('AI_AGENTS.FLOW.OUT_AFTER')"
      type="source"
      :position="Position.Right"
      :class="toolsHandleClass"
      :style="{ top: category ? '40px' : '22px' }"
    />
  </div>

  <!-- ===================== FLUXO (card branco) ===================== -->
  <div
    v-else
    class="relative min-w-[184px] max-w-[240px] rounded-2xl border bg-n-solid-1 px-3 py-2.5 shadow-md transition-all hover:shadow-lg"
    :class="cardClass"
  >
    <span
      v-if="isFloating"
      v-tooltip="t('AI_AGENTS.FLOW.NODE_DISCONNECTED')"
      class="absolute -right-1.5 -top-1.5 z-10 flex size-4 items-center justify-center rounded-full bg-n-amber-9 text-white shadow"
    >
      <span class="i-lucide-unlink text-[9px]" />
    </span>
    <Handle
      v-if="hasInput"
      type="target"
      :position="Position.Left"
      :class="flowHandleClass"
    />
    <div class="flex items-center gap-2.5">
      <span
        class="flex size-8 shrink-0 items-center justify-center rounded-xl"
        :style="{ backgroundColor: tint(color), color }"
      >
        <span :class="meta.icon || 'i-lucide-box'" class="text-base" />
      </span>
      <div class="min-w-0">
        <p class="truncate text-sm font-semibold leading-5 text-n-slate-12">
          {{ title }}
        </p>
        <p
          v-if="data.subtitle"
          class="truncate text-xs leading-4 text-n-slate-10"
        >
          {{ data.subtitle }}
        </p>
      </div>
    </div>
    <div v-if="badges.length" class="mt-2 flex flex-wrap gap-1">
      <span
        v-for="(b, i) in badges"
        :key="i"
        class="rounded-md px-1.5 py-0.5 text-[10px] font-medium leading-3"
        :style="{
          backgroundColor: tint(TONE_HEX[b.tone] || TONE_HEX.default, '24'),
          color: TONE_HEX[b.tone] || TONE_HEX.default,
        }"
      >
        {{ b.label }}
      </span>
    </div>
    <Handle
      v-if="outputs.length"
      type="source"
      :position="Position.Right"
      :class="flowHandleClass"
    />
  </div>
</template>
